/**
 * OppositionPanel - who the party is hiding from, and what each is doing:
 * unaware, investigating, searching, away (or simply hostile, at Alarm).
 *
 * Everyone reads the list. Whoever directs sets each one by hand, or hands
 * them back to the ladder ("Follows the ladder"), and is shown who is
 * SHARPEST - the one a quiet move is tested against - with their number.
 * A player is not shown that: it would be the opposition's sheet.
 *
 * @param {object} props
 * @param {object} props.stealth - `{ state, canDirect, opposition, sharpest, setOpposition }`
 */
import story from '../../styles/Story.module.css';
import styles from '../../styles/Stealth.module.css';
import { OPP_STATES, oppLabel } from '../../engine/stealth/ladder';
import { awarenessOf } from '../../engine/stealth/opposition';

const FOLLOWS = '';

/** The director's hand on one of them: a state, or back to following the ladder. */
function StateSelect({ person, state, onSet }) {
  return (
    <select
      className={story.input}
      aria-label={`What ${person.name} is doing`}
      data-testid={`opp-set-${person.id}`}
      value={state.opposition[person.id] ?? FOLLOWS}
      onChange={(e) => onSet(person.id, e.target.value || null)}
    >
      <option value={FOLLOWS}>Follows the ladder</option>
      {OPP_STATES.map((s) => (
        <option key={s} value={s}>
          {s[0].toUpperCase() + s.slice(1)}
        </option>
      ))}
    </select>
  );
}

function Watcher({ person, stealth }) {
  const { state, canDirect, sharpest } = stealth;
  const label = oppLabel(state, person.id);
  return (
    <li className={story.item} data-testid="opp-row">
      <div className={styles.who}>
        <span className={styles.whoName}>{person.name}</span>
        <span className={styles.state} data-state={label} data-testid={`opp-state-${person.id}`}>
          {label}
        </span>
      </div>
      {canDirect && sharpest?.id === person.id && (
        <span className={styles.sharpest} data-testid="opp-sharpest">
          Sharpest: Awareness {awarenessOf(person)}. Quiet moves are tested against them.
        </span>
      )}
      {/* Never for a hidden figure: its state would name it in the frame every player reads. */}
      {canDirect && !stealth.hiddenIds?.has(person.id) && (
        <StateSelect person={person} state={state} onSet={stealth.setOpposition} />
      )}
    </li>
  );
}

export default function OppositionPanel({ stealth }) {
  return (
    <section className={story.section} aria-label="The opposition" data-testid="opposition-panel">
      <h3 className={story.caption}>The opposition</h3>
      {stealth.opposition.length === 0 && (
        <p className={story.empty} data-testid="opp-none">
          Nobody is here to notice: everyone on the roster is on your side. Add whoever you are hiding from, or name the
          party under Sides.
        </p>
      )}
      <ul className={story.list}>
        {stealth.opposition.map((person) => (
          <Watcher key={person.id} person={person} stealth={stealth} />
        ))}
      </ul>
    </section>
  );
}
